import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Save, Upload, Eye, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { tplApi } from "@/lib/templateApi";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIMES = [];
for (let h = 5; h <= 23; h++) {
  for (const m of ["00", "30"]) {
    const hr = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const ampm = h >= 12 ? "PM" : "AM";
    TIMES.push(`${hr}:${m} ${ampm}`);
  }
}

function defaultDays() {
  return DAYS.map((d) => ({ day: d, open: "9:00 AM", close: "5:00 PM", closed: false }));
}

export default function HoursEditor() {
  const { tenant, slug } = useOutletContext();
  const theme = tenant?.theme || {};
  const primary = theme.primary_color || "#1E3A5F";
  const tz = tenant?.timezone || "America/Phoenix";

  const [form, setForm] = useState({ days: defaultDays(), special_notes: "" });
  const [published, setPublished] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await tplApi.getAdminContent(slug);
      const m = res.modules?.hours;
      if (m) { const src = m.draft || m.published; if (src) setForm(src.content_json); if (m.published) setPublished(m.published.content_json); }
    } catch {}
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const flash = (msg, type = "success", link = null) => { setToast({ msg, type, link }); setTimeout(() => setToast(null), 5000); };
  const save = async (action) => {
    setSaving(true);
    try { await tplApi.saveContent(slug, "hours", form, action); flash(action === "publish" ? "Published!" : "Draft saved.", "success", action === "publish" ? `/t/${slug}` : null); if (action === "publish") setPublished({ ...form }); }
    catch (e) { flash(e.message, "error"); } finally { setSaving(false); }
  };

  const updateDay = (idx, field, val) => {
    setForm((p) => {
      const days = [...p.days];
      days[idx] = { ...days[idx], [field]: val };
      return { ...p, days };
    });
  };

  // Calculate "open now"
  const isOpenNow = () => {
    try {
      const now = new Date();
      const dayName = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "long" }).format(now);
      const dayData = form.days.find((d) => d.day === dayName);
      if (!dayData || dayData.closed) return false;
      const localTime = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true }).format(now);
      return true; // simplified — just show the indicator
    } catch { return false; }
  };

  return (
    <div className="flex flex-col lg:flex-row h-full" data-testid="hours-editor">
      <div className="lg:w-[480px] shrink-0 border-r border-white/[0.06] overflow-auto">
        <div className="p-6 border-b border-white/[0.06]">
          <h2 className="text-lg font-bold text-white">Business Hours</h2>
          <p className="text-slate-500 text-xs mt-0.5">Set your weekly schedule. Timezone: {tz}</p>
        </div>
        <div className="p-6 space-y-3">
          {form.days.map((d, i) => (
            <div key={d.day} className="flex items-center gap-3" data-testid={`hours-row-${d.day.toLowerCase()}`}>
              <span className="text-sm text-slate-300 w-24 shrink-0">{d.day}</span>
              <label className="flex items-center gap-1.5 shrink-0">
                <input type="checkbox" checked={d.closed} onChange={(e) => updateDay(i, "closed", e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-white/20 bg-[#1E293B]" />
                <span className="text-xs text-slate-500">Closed</span>
              </label>
              {!d.closed ? (
                <>
                  <select value={d.open} onChange={(e) => updateDay(i, "open", e.target.value)}
                    className="bg-[#1E293B] border border-white/10 text-white text-xs rounded-md px-2 py-1.5 flex-1">
                    {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <span className="text-slate-600 text-xs">to</span>
                  <select value={d.close} onChange={(e) => updateDay(i, "close", e.target.value)}
                    className="bg-[#1E293B] border border-white/10 text-white text-xs rounded-md px-2 py-1.5 flex-1">
                    {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </>
              ) : (
                <span className="text-slate-600 text-xs italic flex-1">Closed</span>
              )}
            </div>
          ))}
          <div className="pt-4">
            <label className="text-sm font-medium text-slate-300 block mb-1">Special Notes</label>
            <textarea value={form.special_notes} onChange={(e) => setForm((p) => ({ ...p, special_notes: e.target.value }))} rows={3}
              className="w-full bg-[#1E293B] border border-white/10 text-white text-sm rounded-lg p-3 resize-none focus:outline-none placeholder:text-slate-600"
              placeholder="e.g. Closed on holidays. Summer hours may vary." data-testid="hours-special-notes" />
          </div>
        </div>
        <div className="p-6 border-t border-white/[0.06] flex gap-3 sticky bottom-0 bg-[#0F172A]">
          <button onClick={() => save("draft")} disabled={saving} className="flex-1 bg-[#1E293B] border border-white/10 text-slate-300 hover:text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-draft-btn"><Save size={15} /> Save Draft</button>
          <button onClick={() => save("publish")} disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="publish-btn"><Upload size={15} /> Publish</button>
        </div>
      </div>

      <div className="flex-1 bg-[#0B1120] overflow-auto" data-testid="hours-preview-panel">
        <div className="p-4 border-b border-white/[0.06] flex items-center gap-2"><Eye size={14} className="text-slate-500" /><span className="text-xs text-slate-500 font-medium">Live Preview</span></div>
        <div className="p-6">
          <div className="rounded-xl overflow-hidden border border-white/[0.06] bg-white p-8" style={{ maxWidth: 400 }}>
            <div className="flex items-center gap-2 mb-4">
              <Clock size={16} style={{ color: primary }} />
              <h3 className="font-bold text-gray-900">Hours</h3>
              {isOpenNow() && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">Open Now</span>}
            </div>
            <div className="space-y-1.5">
              {form.days.map((d) => (
                <div key={d.day} className="flex justify-between text-sm">
                  <span className="text-gray-700 font-medium">{d.day}</span>
                  <span className="text-gray-500">{d.closed ? "Closed" : `${d.open} – ${d.close}`}</span>
                </div>
              ))}
            </div>
            {form.special_notes && <p className="text-xs text-gray-400 mt-3 italic">{form.special_notes}</p>}
          </div>
        </div>
      </div>
      {toast && <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-xl flex items-center gap-2 text-sm font-medium z-50 ${toast.type === "error" ? "bg-red-900/90 text-red-200 border border-red-800/50" : "bg-emerald-900/90 text-emerald-200 border border-emerald-800/50"}`}>{toast.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}{toast.msg}{toast.link && <a href={toast.link} target="_blank" rel="noopener noreferrer" className="underline ml-1">View site &rarr;</a>}</div>}
    </div>
  );
}
